import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CurrencyDollar,
  CheckCircle,
  WarningCircle,
  ShieldCheck,
  Recycle,
  MapPin,
  Clock,
  Phone,
  Prohibit,
  Scales,
  Leaf,
  CaretRight,
  ArrowRight,
  Info,
} from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { truckLevels } from "@/components/QuoteTool";
import { SITE, PHONE_DISPLAY, PHONE_TEL } from "@/seoData";
import { DUMP_FACILITIES } from "@/seoContentV3";
import MiramarCalculator from "@/components/MiramarCalculator";

const HAUL = [
  { title: "Furniture", desc: "Couches, sectionals, recliners, dressers, tables, desks & office chairs.", img: "https://images.unsplash.com/photo-1573866926487-a1865558a9cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { title: "Appliances", desc: "Refrigerators, washers, dryers, dishwashers, stoves, water heaters & AC units.", img: "https://images.unsplash.com/photo-1663068307522-1731d9c8fbc2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { title: "Garage & Estate Cleanouts", desc: "Years of clutter, boxes, tools and full-property cleanouts cleared in one visit.", img: "https://images.unsplash.com/photo-1635108198322-18c814f9b70f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { title: "Construction Debris", desc: "Drywall, lumber, flooring, tile, concrete & renovation waste.", img: "https://images.unsplash.com/photo-1616551569669-b60598758c4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { title: "Yard Waste", desc: "Tree branches, brush, green waste, sod, fencing & landscaping debris.", img: "https://images.unsplash.com/photo-1667755582571-d7b98b88d326?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { title: "E-Waste", desc: "TVs, monitors, computers, printers & electronics — recycled responsibly.", img: "https://images.unsplash.com/photo-1728610996936-d93900f1886b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
];

// City of San Diego — Miramar Landfill Tier 2 flat-rate fees (FY2027, eff. Jul 1 2026 – Jun 30 2027)
const MIRAMAR_TIER2 = [
  { type: "General Refuse", city: "$148", noncity: "$156" },
  { type: "Special Handling", city: "$207", noncity: "$219" },
  { type: "C&D Debris (construction)", city: "$352", noncity: "$374" },
  { type: "Food Material", city: "$164", noncity: "$164" },
  { type: "Green Material", city: "$109", noncity: "$109" },
  { type: "Clean Wood", city: "$55", noncity: "$55" },
];

// Recycling / special-handling fees for regulated items
const RECYCLE_FEES = [
  { item: "Refrigerators, freezers & A/C units (contain CFCs / freon)", price: "$25 each" },
  { item: "Large appliances — stoves, washers, dryers, hot water heaters", price: "$12 each" },
  { item: "Tires (18\" and under, no rims)", price: "$7 each" },
  { item: "Toilets", price: "$6 each" },
  { item: "Small electronics — printers, keyboards, mice", price: "$0.25 / lb" },
];

// Per-item / single-item pickup pricing
const PER_ITEM = [
  { category: "Appliances", items: [["Refrigerator / Freezer", "$95"], ["Washer & Dryer (pair)", "$135"], ["Washer", "$75"], ["Dryer", "$75"], ["Stove / Oven", "$75"], ["Grill or BBQ", "$65"], ["Hot Tub / Spa", "$200"]] },
  { category: "Mattresses & Beds", items: [["Mattress, Box Spring & Frame", "$95"], ["Mattress & Box Spring", "$85"], ["Mattress (only)", "$65"], ["Box Spring (only)", "$65"], ["Bed Frame", "$65"]] },
  { category: "Couches & Chairs", items: [["Sectional Sofa", "$145"], ["Couch or Sofa", "$95"], ["Loveseat", "$85"], ["Recliner", "$55"], ["Chair (office / arm)", "$25"]] },
  { category: "Tables & Storage", items: [["Large Entertainment Center", "$85"], ["Table & Chairs", "$75"], ["Desk", "$55"], ["Dresser", "$55"], ["Coffee Table", "$45"], ["Table", "$45"], ["Small Entertainment Center", "$45"]] },
  { category: "Electronics", items: [["Office Printer", "$95"], ["TV (any size)", "$40"], ["Computer", "$25"], ["Lamp", "$10"], ["Fax Machine", "$7.50"]] },
  { category: "Miscellaneous", items: [["Piano", "$200"], ["Pool Table", "$200"], ["Exercise Machine", "$55"], ["Lawnmower", "$45"], ["Tool Chest", "$25"], ["Bicycle", "$20"], ["Moving Box", "$15"], ["Large Trash Bag", "$15"]] },
];

const EXTRAS = [
  "2+ flights of stairs",
  "Long carry (over 75 ft)",
  "Disassembly required",
  "Hot tub / spa removal",
  "Piano removal",
  "Shed demolition",
  "Heavy materials (concrete, dirt, tile)",
];

const RISKS = [
  { icon: Prohibit, title: "Illegal dumping is a crime", desc: "Fly-by-night haulers often dump in canyons, alleys or empty lots to skip landfill fees. In California, illegal dumping carries fines up to $10,000+ and possible jail time — and if your paperwork or mail is in that pile, the citation can land on YOU." },
  { icon: ShieldCheck, title: "Uninsured = your liability", desc: "If an uninsured hauler damages your floors, wall or driveway — or gets injured on your property — you can be left holding the bill. A licensed, insured company carries that risk instead of you." },
  { icon: Scales, title: "Unlicensed haulers cut corners", desc: "No license usually means no proper disposal records, no recycling, and no accountability. The 'cheap' quote gets expensive fast when items end up mishandled or your HOA/city comes knocking." },
  { icon: Recycle, title: "Responsible disposal matters", desc: "We sort loads to recycle metal and e-waste, donate usable goods, and take everything to permitted facilities like the Miramar Landfill & Recycling Center — with a paper trail." },
];

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[#46A01E] font-display font-bold uppercase tracking-widest text-xs mb-4">
    <span className="h-px w-8 bg-[#46A01E]" />
    {children}
  </div>
);

export default function PricingGuide() {
  const location = useLocation();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/pricing`)
      .then(({ data }) => setPricing(data))
      .catch(() => {});
  }, []);

  const discount = typeof pricing?.curbsideDiscount === "number" ? pricing.curbsideDiscount : 30;
  const tiers = (pricing?.tiers || truckLevels).map((t) => {
    const base = truckLevels.find((b) => b.label === t.label) || {};
    return { ...base, ...t, curb: Math.round(t.price * (1 - discount / 100)) };
  });
  const perItem = pricing?.perItem || PER_ITEM;
  const recycleFees = pricing?.recycleFees || RECYCLE_FEES;

  const goEstimate = () => navigate("/#estimate");

  const canonical = `${SITE}/pricing-and-disposal-guide`;
  const title = "San Diego Dump & Landfill Fees (2026 Guide) | CleanOut247";
  const description =
    "San Diego dump and landfill fees by facility, plus what junk removal costs instead. Updated for 2026.";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "Junk Removal",
      provider: { "@type": ["MovingCompany", "HomeAndConstructionBusiness"], "@id": SITE + "/#business", name: "CleanOut247", telephone: "+1-619-999-3565", url: SITE, image: SITE + "/og-logo.png", address: { "@type": "PostalAddress", streetAddress: "10335 Reserve Dr", addressLocality: "San Diego", addressRegion: "CA", postalCode: "92127", addressCountry: "US" } },
      areaServed: { "@type": "AdministrativeArea", name: "San Diego County, CA" },
      name: "San Diego Dump & Landfill Fees Guide — CleanOut247",
      description,
      url: canonical,
      offers: tiers.flatMap((t) => [
        { "@type": "Offer", name: `${t.label} (${t.yards}) — Curbside`, price: t.curb, priceCurrency: "USD" },
        { "@type": "Offer", name: `${t.label} (${t.yards}) — Full-Service`, price: t.price, priceCurrency: "USD" },
      ]),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        { "@type": "Question", name: "How much does junk removal cost in San Diego?", acceptedAnswer: { "@type": "Answer", text: "CleanOut247 charges by how much space your items fill in the truck — from $130 for a curbside 1/8 load ($185 full-service) up to $900 for a full 15-cubic-yard load. You always get an upfront, all-in price before we start." } },
        { "@type": "Question", name: "How much does it cost to dump junk at the Miramar Landfill?", acceptedAnswer: { "@type": "Answer", text: "For Tier 2 vehicles (pickup/trailer combos and larger), the City of San Diego Miramar Landfill charges Tier 2 flat rates effective Jul 2026–Jun 2027 (FY2027): General Refuse $148–$156, C&D Debris $352–$374, Green Material $109, and Clean Wood $55 (cars and pickups pay Tier 1 rates from $75), depending on whether waste is generated inside the City. Fees are subject to change — see sandiego.gov." } },
        { "@type": "Question", name: "How much does e-waste disposal cost?", acceptedAnswer: { "@type": "Answer", text: "In California, TVs, monitors and computers are recycled free at certified e-waste recyclers because a recycling fee is already paid at purchase. Electronics may not be placed in the landfill — CleanOut247 takes them to certified recyclers for you." } },
        { "@type": "Question", name: "Why is cheap junk removal risky?", acceptedAnswer: { "@type": "Answer", text: "Unusually cheap, unlicensed or uninsured haulers often skip landfill fees by dumping illegally — which can result in fines up to $10,000 traced back to you, plus liability for property damage or injuries. Licensed, insured companies dispose of waste legally with accountability." } },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "San Diego Dump & Landfill Fees", item: canonical },
      ],
    },
  ];

  return (
    <div className="bg-white text-[#0A0A0A] overflow-x-hidden" data-testid="pricing-guide-page">
      <Seo title={title} description={description} canonical={canonical} jsonLd={jsonLd} />
      <Header />

      {/* HERO */}
      <section className="pt-28 sm:pt-40 lg:pt-48 pb-16 lg:pb-20 px-5 lg:px-8 bg-[#0B1E45] text-white">
        <div className="max-w-7xl mx-auto">
          <Eyebrow>Dump & Landfill Fees</Eyebrow>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
            San Diego Dump &amp; Landfill Fees: 2026 Guide
          </h1>
          <p className="text-white/70 mt-6 text-lg leading-relaxed max-w-2xl">
            Planning a San Diego dump run? Here are the San Diego landfill fees by facility for 2026,
            what it really costs to dispose of junk yourself, and what hiring junk removal costs instead —
            so you can pick the cheaper option for your load.
          </p>
          <p className="text-white/40 text-sm mt-3" data-testid="pricing-last-updated">Last updated: June 2026</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <button
              data-testid="pricing-hero-estimate-btn"
              onClick={goEstimate}
              className="flex items-center gap-2 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm px-6 py-4 rounded-sm transition-colors"
            >
              Get My Instant Estimate <ArrowRight size={18} weight="bold" />
            </button>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="pricing-hero-call-btn"
              className="flex items-center gap-2 border border-white/25 hover:bg-white/10 text-white font-display font-extrabold uppercase tracking-wide text-sm px-6 py-4 rounded-sm transition-colors"
            >
              <Phone size={18} weight="fill" /> Call/Text {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 1 — PRICING */}
      <section className="px-5 lg:px-8 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto">
          <div data-testid="minimum-order-notice" className="mb-10 flex items-start gap-3 bg-[#EAF4E3] border-l-4 border-[#46A01E] rounded-sm px-5 py-4">
            <Info size={22} weight="fill" className="text-[#46A01E] shrink-0 mt-0.5" />
            <p className="text-[#15306E] text-sm sm:text-base">
              <strong>Please note:</strong> Minimum pick-up order is <strong>2 cubic yards</strong> (a 1/8 load):{" "}
              <strong data-testid="minimum-curbside-price">${tiers[0]?.curb}</strong> curbside or{" "}
              <strong data-testid="minimum-full-price">${tiers[0]?.price}</strong> full-service.
            </p>
          </div>
          <div className="max-w-2xl mb-12">
            <Eyebrow>Transparent Pricing</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
              You pay for the space you use — that's it.
            </h2>
            <p className="text-[#52525B] mt-4 text-lg leading-relaxed">
              Pricing is based on how much room your items take up in our 15-cubic-yard truck — the same
              prices as our instant quote tool. We give you a firm, all-in price on-site before we lift a
              finger. No weight surprises, no "gotcha" fees.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="service-type-explainer">
            <div className="border-l-4 border-[#46A01E] bg-[#F4FAF0] rounded-sm p-5">
              <h3 className="font-display font-extrabold text-[#15306E]">Curbside · save {discount}%</h3>
              <p className="text-sm text-[#3F3F46] mt-1.5 leading-relaxed">You stage items at a drive-up spot — driveway, open garage or alley. Our crew loads the truck. You don't need to be home.</p>
            </div>
            <div className="border-l-4 border-[#15306E] bg-[#F1F4FA] rounded-sm p-5">
              <h3 className="font-display font-extrabold text-[#15306E]">Full-Service</h3>
              <p className="text-sm text-[#3F3F46] mt-1.5 leading-relaxed">You point, we carry — from inside, upstairs or the backyard. All labor is in the price, with 2+ crew for bulky pieces.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="pricing-tiers">
            {tiers.map((t) => (
              <div
                key={t.label}
                data-testid={`pricing-tier-${t.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
                className="border border-[#E4E4E7] rounded-sm p-4 text-center hover:border-[#46A01E] hover:shadow-md transition-all"
              >
                <div className="h-1.5 w-10 mx-auto rounded-full mb-3" style={{ backgroundColor: t.color }} />
                <div className="font-display font-extrabold text-[#15306E] text-sm">{t.label}</div>
                <div className="text-[11px] text-[#52525B] mb-3">{t.yards}</div>
                <div className="text-[10px] uppercase tracking-wider text-[#71717A] font-bold">Curbside</div>
                <div data-testid={`tier-curbside-${t.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} className="font-display font-black text-2xl text-[#46A01E] leading-tight">${t.curb}</div>
                <div className="h-px bg-[#E4E4E7] my-2" />
                <div className="text-[10px] uppercase tracking-wider text-[#71717A] font-bold">Full-Service</div>
                <div data-testid={`tier-full-${t.label.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} className="font-display font-black text-lg text-[#15306E] leading-tight">${t.price}</div>
              </div>
            ))}
          </div>

          <figure className="mt-10 grid lg:grid-cols-5 gap-6 items-center bg-[#F4F4F5] rounded-sm p-4 lg:p-6" data-testid="cubic-yard-visual">
            <img src="/images/one-cubic-yard.webp" width="1200" height="800" loading="lazy"
              alt="One cubic yard of household junk — trash bags, moving boxes and an office chair packed into a 3 ft × 3 ft × 3 ft space, beside a person for scale"
              className="lg:col-span-3 w-full h-auto rounded-sm" />
            <figcaption className="lg:col-span-2">
              <h3 className="font-display font-black tracking-tighter text-2xl text-[#15306E]">What does 1 cubic yard look like?</h3>
              <p className="text-[#3F3F46] mt-3 leading-relaxed">
                One cubic yard is a space 3 ft wide, 3 ft deep and 3 ft tall — about hip-high on an adult. It holds
                about 4 full trash bags, 6–7 moving boxes and an office chair, packed tight.
              </p>
              <ul className="mt-4 space-y-1.5 text-sm text-[#3F3F46]">
                <li><strong>2 cu yds (1/8 load):</strong> a loveseat and a few bags, or a mattress set</li>
                <li><strong>7.5 cu yds (1/2 load):</strong> a full bedroom set plus boxes</li>
                <li><strong>15 cu yds (full truck):</strong> a whole garage or small apartment</li>
              </ul>
            </figcaption>
          </figure>

          <div className="grid lg:grid-cols-2 gap-6 mt-10">
            <div className="bg-[#F4FAF0] border border-[#DCEAD0] rounded-sm p-6">
              <h3 className="font-display font-extrabold text-lg text-[#15306E] flex items-center gap-2 mb-4">
                <CheckCircle size={22} weight="fill" className="text-[#46A01E]" /> Always included
              </h3>
              <ul className="space-y-2.5 text-sm text-[#3F3F46]">
                {["All labor & loading — we do the lifting", "Sweep-up after we load", "Fuel & drive time", "Recycling, donation & legal disposal", "Upfront, no-hidden-fees quote"].map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle size={16} weight="fill" className="text-[#46A01E] mt-0.5 shrink-0" /> {i}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#FFF8F0] border border-[#F5E2C8] rounded-sm p-6">
              <h3 className="font-display font-extrabold text-lg text-[#15306E] flex items-center gap-2 mb-4">
                <Info size={22} weight="fill" className="text-[#E08A1E]" /> Situations that may add cost
              </h3>
              <ul className="grid sm:grid-cols-2 gap-x-4 gap-y-2 text-sm text-[#3F3F46]">
                {EXTRAS.map((i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CaretRight size={15} weight="bold" className="text-[#E08A1E] mt-0.5 shrink-0" /> {i}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-[#71717A] mt-4">Any of these are always quoted upfront — you approve the price before we start.</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 1B — PER-ITEM PRICING */}
      <section className="px-5 lg:px-8 pb-20 lg:pb-28 -mt-6">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-10">
            <Eyebrow>Single-Item & Curbside Pricing</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
              Just one or two things? Pay per item, but consider the minimum order of ${tiers[0]?.curb} curbside (${tiers[0]?.price} full-service).
            </h2>
            <p className="text-[#52525B] mt-4 text-lg leading-relaxed">
              Only getting rid of a couch or a fridge? Skip the load-based pricing and pay a flat,
              per-item rate. Stage it curbside, in the driveway or by your dumpster and we'll grab it.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="per-item-pricing">
            {perItem.map((group) => (
              <div key={group.category} data-testid={`per-item-${group.category.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} className="border border-[#E4E4E7] rounded-sm overflow-hidden">
                <div className="bg-[#F4F4F5] px-5 py-3 font-display font-extrabold text-[#15306E] text-sm uppercase tracking-wide">
                  {group.category}
                </div>
                <ul>
                  {group.items.map(([name, price]) => (
                    <li key={name} className="flex items-center justify-between gap-3 px-5 py-2.5 border-t border-[#F0F0F1] text-sm">
                      <span className="text-[#3F3F46]">{name}</span>
                      <span className="font-display font-bold text-[#46A01E] shrink-0">{price}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="text-sm text-[#71717A] mt-6">
            Don't see your item? <a href={`tel:${PHONE_TEL}`} className="text-[#15306E] font-semibold underline hover:text-[#46A01E]">Call or text {PHONE_DISPLAY}</a> for a quick quote. Multiple items may qualify for lower load-based pricing above — we'll always give you the better deal.
          </p>
        </div>
      </section>

      {/* SECTION 2 — WHAT WE HAUL */}
      <section className="px-5 lg:px-8 py-20 lg:py-28 bg-[#F4F4F5]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-12">
            <Eyebrow>What We Haul</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
              If it's not hazardous, it's probably going.
            </h2>
            <p className="text-[#52525B] mt-4 text-lg leading-relaxed">
              From a single couch to an entire estate, here's the kind of stuff we clear every day
              across San Diego County.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="haul-grid">
            {HAUL.map((h) => (
              <div key={h.title} data-testid={`haul-${h.title.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} className="bg-white rounded-sm overflow-hidden border border-[#E4E4E7] group">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={h.img} alt={h.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-extrabold text-lg text-[#15306E]">{h.title}</h3>
                  <p className="text-sm text-[#52525B] mt-1.5 leading-relaxed">{h.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — SD DISPOSAL COSTS */}
      <section className="px-5 lg:px-8 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mb-12">
            <Eyebrow>What Disposal Really Costs</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-[#15306E]">
              San Diego disposal fees, explained.
            </h2>
            <p className="text-[#52525B] mt-4 text-lg leading-relaxed">
              A big part of any junk price is the tipping fee at the landfill. Here are the City of
              San Diego's official <strong>Miramar Landfill</strong> flat rates for{" "}
              <strong>Tier 2 vehicles</strong> (pickup/trailer combos, trailers over 5′W × 9′L × 4′H,
              and medium/heavy trucks) — the class most junk-removal trucks fall under.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Fee table */}
            <div className="lg:col-span-2 border border-[#E4E4E7] rounded-sm overflow-hidden">
              <div className="bg-[#15306E] text-white px-5 py-4 flex items-center gap-2">
                <CurrencyDollar size={20} weight="fill" className="text-[#5DB82F]" />
                <span className="font-display font-extrabold">Miramar Landfill — Tier 2 Flat Rates</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="miramar-fee-table">
                  <thead>
                    <tr className="bg-[#F4F4F5] text-[#15306E] text-left">
                      <th className="px-5 py-3 font-display font-bold">Waste Type</th>
                      <th className="px-5 py-3 font-display font-bold text-right">City Waste</th>
                      <th className="px-5 py-3 font-display font-bold text-right">Non-City Waste</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MIRAMAR_TIER2.map((row) => (
                      <tr key={row.type} className="border-t border-[#E4E4E7]">
                        <td className="px-5 py-3 text-[#3F3F46]">{row.type}</td>
                        <td className="px-5 py-3 text-right font-semibold text-[#15306E]">{row.city}</td>
                        <td className="px-5 py-3 text-right font-semibold text-[#15306E]">{row.noncity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-4 bg-[#FAFAFA] border-t border-[#E4E4E7] text-xs text-[#71717A] leading-relaxed">
                <Info size={13} weight="fill" className="inline text-[#46A01E] mr-1" />
                Fees effective Jul 1, 2026 – Jun 30, 2027 and are based on vehicle type, waste type,
                net weight, and where the waste was generated. Uncovered loads incur a $50 fee. Fees are
                subject to change —{" "}
                <a href="https://www.sandiego.gov/environmental-services/miramar/fees" target="_blank" rel="noreferrer" className="text-[#15306E] underline hover:text-[#46A01E]">
                  verify current rates at sandiego.gov
                </a>.
              </div>
            </div>

            {/* E-waste + HHW + Location */}
            <div className="space-y-6">
              <div className="border border-[#E4E4E7] rounded-sm p-5">
                <h3 className="font-display font-extrabold text-[#15306E] flex items-center gap-2 mb-2">
                  <Recycle size={20} weight="fill" className="text-[#46A01E]" /> E-Waste
                </h3>
                <p className="text-sm text-[#52525B] leading-relaxed">
                  In California, <strong>TVs, monitors, computers & electronics are recycled free</strong> —
                  a recycling fee is already paid when you buy them. Electronics are <strong>banned from the
                  landfill</strong>, so they must go to a certified e-waste recycler (we handle this for you).
                </p>
              </div>
              <div className="border border-[#E4E4E7] rounded-sm p-5">
                <h3 className="font-display font-extrabold text-[#15306E] flex items-center gap-2 mb-2">
                  <Leaf size={20} weight="fill" className="text-[#46A01E]" /> Hazardous Waste
                </h3>
                <p className="text-sm text-[#52525B] leading-relaxed">
                  Paint, batteries, chemicals, fluorescent tubes and CRTs <strong>can't go in the landfill</strong>.
                  City of San Diego residents can drop household hazardous waste free at the City's HHW facility
                  by appointment.
                </p>
              </div>
              <div className="bg-[#0B1E45] text-white rounded-sm p-5">
                <h3 className="font-display font-extrabold flex items-center gap-2 mb-3">
                  <MapPin size={20} weight="fill" className="text-[#5DB82F]" /> Miramar Landfill
                </h3>
                <p className="text-sm text-white/80">5180 Convoy St, San Diego, CA 92111</p>
                <p className="text-sm text-white/80 flex items-center gap-2 mt-2"><Clock size={15} weight="fill" className="text-[#5DB82F]" /> Mon–Sat · closed Sundays & holidays</p>
                <p className="text-sm text-white/80 flex items-center gap-2 mt-1"><Phone size={15} weight="fill" className="text-[#5DB82F]" /> 858-694-7000</p>
                <p className="text-xs text-white/50 mt-3">No hand unloads after 4 p.m. All loads must be covered.</p>
              </div>
            </div>
          </div>

          {/* Recycling / special-handling fees */}
          <div className="mt-8 border border-[#E4E4E7] rounded-sm overflow-hidden">
            <div className="bg-[#15306E] text-white px-5 py-4 flex items-center gap-2">
              <Recycle size={20} weight="fill" className="text-[#5DB82F]" />
              <span className="font-display font-extrabold">Appliance & E-Waste Recycling Fees</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="recycle-fee-table">
                <thead>
                  <tr className="bg-[#F4F4F5] text-[#15306E] text-left">
                    <th className="px-5 py-3 font-display font-bold">Item</th>
                    <th className="px-5 py-3 font-display font-bold text-right">Recycling Fee</th>
                  </tr>
                </thead>
                <tbody>
                  {recycleFees.map((row) => (
                    <tr key={row.item} className="border-t border-[#E4E4E7]">
                      <td className="px-5 py-3 text-[#3F3F46]">{row.item}</td>
                      <td className="px-5 py-3 text-right font-semibold text-[#15306E] whitespace-nowrap">{row.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-5 py-4 bg-[#FAFAFA] border-t border-[#E4E4E7] text-xs text-[#71717A] leading-relaxed">
              <Info size={13} weight="fill" className="inline text-[#46A01E] mr-1" />
              Items like refrigerators and A/C units require certified freon/CFC removal, and electronics
              must be processed by certified recyclers — these fees cover that special handling. With
              CleanOut247, recycling is bundled into your quoted price.
            </div>
          </div>

          <div className="mt-8 bg-[#F4FAF0] border border-[#DCEAD0] rounded-sm p-6 flex items-start gap-3">
            <ShieldCheck size={26} weight="fill" className="text-[#46A01E] shrink-0 mt-0.5" />
            <p className="text-sm text-[#3F3F46] leading-relaxed">
              <strong>The good news:</strong> when you hire CleanOut247, the tipping fees, sorting, recycling
              and e-waste drop-offs are all bundled into your flat price — plus we do all the loading.
              No landfill lines, no covering your own load, no per-item guesswork.
            </p>
          </div>

          <MiramarCalculator />

          {/* County facilities + couch dump + Miramar link */}
          <div className="mt-10 grid lg:grid-cols-2 gap-6">
            <div className="border border-[#E4E4E7] rounded-sm p-6" data-testid="dump-facilities">
              <h3 className="font-display font-extrabold text-xl text-[#15306E] mb-3">San Diego dump fees by facility</h3>
              <ul className="space-y-3">
                {DUMP_FACILITIES.map((f) => (
                  <li key={f.name} className="text-sm text-[#52525B] leading-relaxed">
                    <strong className="text-[#15306E]">{f.name}</strong> — {f.where}. {f.notes}{f.phone ? ` Phone: ${f.phone}.` : ""}{f.guide && <> <a href={f.guide} data-testid={`facility-guide-${f.guide.split("/").pop()}`} className="text-[#46A01E] font-semibold underline">Full guide</a></>}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-[#71717A] mt-3">Rates change every fiscal year — confirm with the facility before you go, or skip the trip and let us haul it from $130.</p>
            </div>
            <div className="border border-[#E4E4E7] rounded-sm p-6">
              <h3 className="font-display font-extrabold text-xl text-[#15306E] mb-3">How much does it cost to dump a couch in San Diego?</h3>
              <p className="text-sm text-[#52525B] leading-relaxed">
                Hauling a couch to the landfill yourself means the gate fee plus a truck, gas, and the
                heavy lifting. For most people it's faster and often cheaper to book{" "}
                <a href="/services/couch-removal" className="text-[#46A01E] font-semibold underline hover:text-[#3A8418]">couch removal in San Diego</a>{" "}
                — we lift it, load it, and dispose of it for a flat ${PER_ITEM[2].items[1][1].replace("$","")} single-item rate.
              </p>
              <h3 className="font-display font-extrabold text-xl text-[#15306E] mt-6 mb-3">Dump it yourself vs. hire junk removal</h3>
              <p className="text-sm text-[#52525B] leading-relaxed">
                A DIY dump run can pencil out for one light load if you already have a truck. Once you add
                gate fees, fuel, your time and the lifting, hiring out usually wins for anything bulky or
                more than a few items. For a deeper breakdown, see our{" "}
                <a href="/junk-removal-cost" className="text-[#46A01E] font-semibold underline hover:text-[#3A8418]">San Diego junk removal cost guide</a>.
              </p>
            </div>
          </div>

          <div className="mt-6 bg-[#F4F4F5] border border-[#E4E4E7] rounded-sm p-6">
            <p className="text-sm text-[#3F3F46] leading-relaxed">
              <strong>Heading to Miramar specifically?</strong> Our dedicated{" "}
              <a href="/blog/miramar-landfill-guide" className="text-[#15306E] font-semibold underline hover:text-[#46A01E]">Miramar Landfill fees and hours</a>{" "}
              guide covers the address, hours, accepted items and full Tier-2 fee schedule for the City of San Diego's main landfill.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHY CHEAP COSTS MORE */}
      <section className="px-5 lg:px-8 py-20 lg:py-28 bg-[#0B1E45] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <Eyebrow>Why "Cheap" Costs More</Eyebrow>
              <h2 className="font-display font-black tracking-tighter text-4xl lg:text-5xl">
                The cheapest quote can become the most expensive mistake.
              </h2>
              <p className="text-white/70 mt-5 text-lg leading-relaxed">
                A suspiciously low price usually means someone is cutting a corner you'll pay for later.
                Here's what to watch out for — and why licensed, insured, accountable matters.
              </p>
              <div className="mt-8 rounded-sm overflow-hidden border border-white/10">
                <img
                  src="https://images.unsplash.com/photo-1602262442764-c14f8db98045?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                  alt="Illegally dumped trash on a roadside"
                  loading="lazy"
                  className="w-full h-56 object-cover"
                />
              </div>
              <p className="text-xs text-white/40 mt-2">Illegal dumping — where "cheap" junk too often ends up.</p>
            </div>

            <div className="space-y-4">
              {RISKS.map((r) => (
                <div key={r.title} data-testid={`risk-${r.title.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`} className="bg-white/[0.04] border border-white/10 rounded-sm p-5 flex gap-4">
                  <r.icon size={30} weight="duotone" className="text-[#5DB82F] shrink-0" />
                  <div>
                    <h3 className="font-display font-extrabold text-lg">{r.title}</h3>
                    <p className="text-sm text-white/70 mt-1.5 leading-relaxed">{r.desc}</p>
                  </div>
                </div>
              ))}
              <div className="bg-[#46A01E] rounded-sm p-6">
                <div className="flex items-center gap-2 font-display font-extrabold text-lg mb-2">
                  <ShieldCheck size={22} weight="fill" /> CleanOut247 is licensed & insured
                </div>
                <p className="text-sm text-white/90 leading-relaxed">
                  Real insurance, legal disposal, and a company that stands behind the work —
                  so the only thing that disappears is your junk.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-5 lg:px-8 py-16 lg:py-20 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-display font-black tracking-tighter text-3xl lg:text-4xl text-[#15306E]">
            Ready for an honest, upfront price?
          </h2>
          <p className="text-[#52525B] mt-3">Same-day service across San Diego County — 7 days a week.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-7">
            <button
              data-testid="pricing-cta-estimate-btn"
              onClick={goEstimate}
              className="flex items-center gap-2 bg-[#15306E] hover:bg-[#46A01E] text-white font-display font-extrabold uppercase tracking-wide text-sm px-6 py-4 rounded-sm transition-colors"
            >
              Get My Instant Estimate <ArrowRight size={18} weight="bold" />
            </button>
            <a
              href={`tel:${PHONE_TEL}`}
              className="flex items-center gap-2 border border-[#E4E4E7] hover:border-[#46A01E] text-[#15306E] font-display font-extrabold uppercase tracking-wide text-sm px-6 py-4 rounded-sm transition-colors"
            >
              <Phone size={18} weight="fill" /> Call/Text {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
